import { useState, useEffect, useRef } from 'react';
import type { IntakeRecord } from '../store/slices/intakeSlice';

interface UseIntakeFormSyncOptions<T extends Record<string, unknown>> {
  selectedIntake: IntakeRecord | null;
  extract: (intake: IntakeRecord) => T;
  /**
   * When true, skip re-syncing on same-intake store updates.
   * Intake switches (new ID) always re-hydrate regardless of this flag.
   */
  skipResync?: boolean;
  /**
   * When true, only overwrite fields whose local value still matches the last
   * synced server value (i.e. the user hasn't edited them). This mirrors
   * react-hook-form's `keepDirtyValues` semantics for useState-based forms.
   */
  keepDirtyValues?: boolean;
}

/**
 * Syncs local form state from the Zustand `selectedIntake` store value.
 *
 * Handles:
 * - Full re-hydration on intake switch (new intake ID)
 * - Field-level "keepDirtyValues" merge on same-intake updates (opt-in)
 * - Complete skip of re-syncs while a particular tab is editing (opt-in)
 *
 * Replaces the duplicated `initializedIntakeIdRef` / `hydratedIntakeIdRef`
 * patterns that were scattered across Intake3, EligibilityCheckStep, and
 * PriorAuthStep.
 */
export function useIntakeFormSync<T extends Record<string, unknown>>({
  selectedIntake,
  extract,
  skipResync = false,
  keepDirtyValues = false,
}: UseIntakeFormSyncOptions<T>): [T, React.Dispatch<React.SetStateAction<T>>] {
  // Keep extract in a ref so the effect doesn't re-run when the caller
  // passes an inline function.
  const extractRef = useRef(extract);
  extractRef.current = extract;

  const [formData, setFormData] = useState<T>(() =>
    selectedIntake ? extractRef.current(selectedIntake) : ({} as T)
  );

  const lastIntakeIdRef = useRef<string | null>(null);
  const lastExtractedRef = useRef<T | null>(null);

  useEffect(() => {
    if (!selectedIntake) return;

    const newValues = extractRef.current(selectedIntake);

    // Intake switch → always full re-hydrate
    if (lastIntakeIdRef.current !== selectedIntake.id) {
      lastIntakeIdRef.current = selectedIntake.id;
      lastExtractedRef.current = newValues;
      setFormData(newValues);
      return;
    }

    if (skipResync) return;

    const lastExtracted = lastExtractedRef.current;
    lastExtractedRef.current = newValues;

    if (keepDirtyValues && lastExtracted) {
      setFormData(prev => {
        const merged = { ...prev };
        let hasChanges = false;
        for (const key of Object.keys(newValues) as Array<keyof T>) {
          // Only overwrite if the local value still equals what we last
          // synced from the server (field is "clean").
          if (
            prev[key] === lastExtracted[key] &&
            prev[key] !== newValues[key]
          ) {
            (merged as Record<string, unknown>)[key as string] = newValues[key];
            hasChanges = true;
          }
        }
        return hasChanges ? merged : prev;
      });
    } else {
      setFormData(newValues);
    }
  }, [selectedIntake, skipResync, keepDirtyValues]);

  return [formData, setFormData];
}
